import { useEffect, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client/react'
import Button from '../../components/ui/Button'
import FormError from '../../components/ui/FormError'
import Input from '../../components/ui/Input'
import ProviderSettingsLayout from '../../components/provider/ProviderSettingsLayout'
import {
  MY_PROVIDER_MANAGEMENT_QUERY,
  MY_PROVIDER_PUBLIC_AREAS_QUERY,
  SET_SERVICE_AREAS_MUTATION,
  type ManagedPublicAreasData,
  type ManagedPublicAreasVariables,
  type ManagedServiceArea,
  type MyProviderManagementData,
  type SetServiceAreasData,
  type SetServiceAreasVariables,
} from '../../graphql/providerManagement'
import { useLang, useScopedTranslations } from '../../contexts/LangContext'

interface AreaDraft {
  id: string
  postalCode: string
  areaName: string
  city: string
}

const translations = {
  el: {
    title: 'Περιοχές εξυπηρέτησης',
    body: 'Δήλωσε τους ταχυδρομικούς κώδικες και τις ονομασίες περιοχών που αναλαμβάνεις.',
    loading: 'Φορτώνουμε τις περιοχές...',
    noAreas: 'Δεν έχεις προσθέσει ακόμη περιοχές.',
    save: 'Αποθήκευση περιοχών',
    saved: 'Οι περιοχές ενημερώθηκαν.',
    postalCode: 'Ταχυδρομικός κώδικας',
    areaName: 'Όνομα περιοχής',
    city: 'Πόλη',
    add: 'Προσθήκη περιοχής',
    remove: 'Αφαίρεση',
    addError: 'Συμπλήρωσε τουλάχιστον έγκυρο ταχυδρομικό κώδικα.',
    preloadWarning: 'Το τρέχον API δεν μπορεί ακόμη να ξαναφορτώσει υπάρχουσες περιοχές για μη δημόσια προφίλ. Μια νέα αποθήκευση θα αντικαταστήσει την τρέχουσα λίστα.',
  },
  en: {
    title: 'Service areas',
    body: 'Define the postal codes and area labels you cover.',
    loading: 'Loading service areas...',
    noAreas: 'No service areas added yet.',
    save: 'Save areas',
    saved: 'Areas updated.',
    postalCode: 'Postal code',
    areaName: 'Area label',
    city: 'City',
    add: 'Add area',
    remove: 'Remove',
    addError: 'Enter at least a valid postal code.',
    preloadWarning: 'The current API cannot yet reload existing areas for non-public profiles. A new save will replace the current list.',
  },
} as const

function randomId() {
  return Math.random().toString(36).slice(2, 10)
}

export default function AreasPage() {
  const { locale, t } = useLang()
  const copy = useScopedTranslations('provider.areas', translations[locale])

  const [areas, setAreas] = useState<AreaDraft[]>([])
  const [newArea, setNewArea] = useState({ postalCode: '', areaName: '', city: 'Athens' })
  const [errors, setErrors] = useState<string[]>([])
  const [saved, setSaved] = useState(false)

  const { data: myData, loading: loadingProfile } = useQuery<MyProviderManagementData>(MY_PROVIDER_MANAGEMENT_QUERY)
  const providerId = myData?.myProviderProfile?.id
  const { data: publicData, loading: loadingPublic } = useQuery<ManagedPublicAreasData, ManagedPublicAreasVariables>(
    MY_PROVIDER_PUBLIC_AREAS_QUERY,
    {
      variables: { id: providerId ?? '' },
      skip: !providerId,
    },
  )
  const [setServiceAreas, { loading: saving }] = useMutation<SetServiceAreasData, SetServiceAreasVariables>(SET_SERVICE_AREAS_MUTATION)
  const publicProfileUnavailable = Boolean(providerId) && !loadingPublic && !publicData?.providerProfile

  useEffect(() => {
    const serviceAreas = publicData?.providerProfile?.serviceAreas ?? []
    setAreas(serviceAreas.map((area: ManagedServiceArea) => ({
      id: area.id,
      postalCode: area.postalCode,
      areaName: area.areaName ?? '',
      city: area.city,
    })))
  }, [publicData])

  function addArea() {
    setErrors([])
    setSaved(false)

    const postalCode = newArea.postalCode.replace(/\D/g, '').slice(0, 5)
    if (postalCode.length !== 5) {
      setErrors([copy.addError])
      return
    }

    setAreas((current) => [
      ...current,
      {
        id: randomId(),
        postalCode,
        areaName: newArea.areaName.trim(),
        city: newArea.city.trim() || 'Athens',
      },
    ])
    setNewArea({ postalCode: '', areaName: '', city: 'Athens' })
  }

  async function handleSave() {
    setErrors([])
    setSaved(false)

    try {
      const { data } = await setServiceAreas({
        variables: {
          areas: areas.map((area) => ({
            postalCode: area.postalCode,
            areaName: area.areaName || undefined,
            city: area.city || undefined,
          })),
        },
      })

      const mutationErrors = data?.setServiceAreas.errors ?? [t('Δεν ήταν δυνατή η αποθήκευση.', 'Could not save areas.')]
      if (mutationErrors.length > 0) {
        setErrors(mutationErrors)
        return
      }

      setSaved(true)
    } catch {
      setErrors([t('Δεν ήταν δυνατή η αποθήκευση.', 'Could not save areas.')])
    }
  }

  return (
    <ProviderSettingsLayout title={copy.title} body={copy.body}>
      <section className="rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm">
        {loadingProfile || loadingPublic ? (
          <p className="text-sm text-slate-500">{copy.loading}</p>
        ) : (
          <>
            <FormError errors={errors} />
            {publicProfileUnavailable && (
              <div className="mb-4 rounded-xl border border-amber-100 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-800">
                {copy.preloadWarning}
              </div>
            )}
            {saved && (
              <div className="mb-4 rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-700">
                {copy.saved}
              </div>
            )}

            <div className="space-y-4">
              {areas.length === 0 && (
                <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-5 text-sm text-slate-500">
                  {copy.noAreas}
                </div>
              )}

              {areas.map((area) => (
                <article key={area.id} className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-5">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <h2 className="text-lg font-bold text-slate-950">{area.areaName || area.city}</h2>
                      <p className="mt-2 text-sm text-slate-500">{area.city} · {area.postalCode}</p>
                    </div>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => {
                        setSaved(false)
                        setAreas((current) => current.filter((item) => item.id !== area.id))
                      }}
                    >
                      {copy.remove}
                    </Button>
                  </div>
                </article>
              ))}
            </div>

            <div className="mt-8 rounded-[1.5rem] border border-slate-200 bg-white p-5 shadow-sm">
              <div className="grid gap-4 md:grid-cols-3">
                <Input
                  label={copy.postalCode}
                  inputMode="numeric"
                  value={newArea.postalCode}
                  onChange={(event) => setNewArea((current) => ({ ...current, postalCode: event.target.value.replace(/\D/g, '').slice(0, 5) }))}
                />
                <Input
                  label={copy.areaName}
                  value={newArea.areaName}
                  onChange={(event) => setNewArea((current) => ({ ...current, areaName: event.target.value }))}
                />
                <Input
                  label={copy.city}
                  value={newArea.city}
                  onChange={(event) => setNewArea((current) => ({ ...current, city: event.target.value }))}
                />
              </div>

              <div className="mt-5 flex justify-end">
                <Button type="button" variant="secondary" onClick={addArea}>
                  {copy.add}
                </Button>
              </div>
            </div>

            <div className="mt-8 flex justify-end">
              <Button type="button" size="lg" loading={saving} onClick={handleSave}>
                {copy.save}
              </Button>
            </div>
          </>
        )}
      </section>
    </ProviderSettingsLayout>
  )
}
